import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { GoogleIcon } from "@/ui/components/icons/social/Google";
import { AppleIcon } from "@/ui/components/icons/social/Apple";

const socialLoginButtonVariants = cva(
  "inline-flex items-center justify-center gap-[1rem] rounded-[0.3125rem] font-montserrat font-normal text-[0.9375rem] text-center text-white tracking-[0.3px] bg-gray-800 hover:bg-gray-700 active:bg-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-600 focus-visible:ring-offset-2 transition-colors disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        google: "",
        apple: "",
      },
      size: {
        default: "h-[2.875rem] px-6 py-3",
      },
    },
    defaultVariants: {
      variant: "google",
      size: "default",
    },
  }
);

export interface SocialLoginButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof socialLoginButtonVariants> {
  /**
   * Alternative text for screen readers
   */
  "aria-label"?: string;
}

/**
 * Social Login Button Component
 *
 * A button designed specifically for social authentication (Google and Apple Sign In).
 * Both variants share identical styling - only the icon and text content differ.
 * Follows ANYW3AR design system with proper icon placement and styling.
 *
 * @example
 * ```tsx
 * <SocialLoginButton variant="google" onClick={handleGoogleSignIn}>
 *   Sign in with Google
 * </SocialLoginButton>
 *
 * <SocialLoginButton variant="apple" onClick={handleAppleSignIn}>
 *   Sign in with Apple
 * </SocialLoginButton>
 * ```
 */
const SocialLoginButton = React.forwardRef<
  HTMLButtonElement,
  SocialLoginButtonProps
>(({ className, variant, size, children, ...props }, ref) => {
  // Render appropriate icon based on variant
  const icon =
    variant === "apple" ? <AppleIcon size={20} /> : <GoogleIcon size={20} />;

  return (
    <button
      className={cn(socialLoginButtonVariants({ variant, size, className }))}
      ref={ref}
      {...props}
    >
      {icon}
      <span>{children}</span>
    </button>
  );
});

SocialLoginButton.displayName = "SocialLoginButton";

export { SocialLoginButton, socialLoginButtonVariants };
